import type { ComponentProps, ReactNode } from "react";
import {
  Header,
  MenuItem as Item,
  Menu as M,
  MenuSection,
  MenuTrigger,
  Popover,
  Pressable,
  Separator,
} from "react-aria-components";
import { cn } from "./utils";

const itemClass =
  "flex cursor-default items-center gap-2 rounded-[5px] px-2 py-1.5 text-[13px] outline-none select-none data-[disabled]:opacity-50 data-[focused]:bg-muted [&_svg]:size-4 [&_svg]:text-fg-subtle";

/** `trigger` must be a single focusable element that forwards its ref, like a button. */
export function Menu({
  trigger,
  children,
  align = "end",
}: {
  trigger: ReactNode;
  children: ReactNode;
  align?: "start" | "end";
}) {
  return (
    <MenuTrigger>
      <Pressable>{trigger as ComponentProps<typeof Pressable>["children"]}</Pressable>
      <Popover
        placement={align === "start" ? "bottom start" : "bottom end"}
        offset={6}
        className="animate-fade-in z-50 min-w-44 rounded-md border border-border bg-bg p-1 shadow-md outline-none"
      >
        <M className="outline-none">{children}</M>
      </Popover>
    </MenuTrigger>
  );
}

export function MenuItem({
  children,
  onSelect,
  danger,
  icon,
  disabled,
}: {
  children: ReactNode;
  onSelect?: () => void;
  danger?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <Item
      isDisabled={disabled}
      onAction={onSelect}
      textValue={typeof children === "string" ? children : undefined}
      className={cn(itemClass, danger && "text-danger [&_svg]:text-danger")}
    >
      {icon}
      {children}
    </Item>
  );
}

/** An on/off item (menuitemcheckbox) that keeps the menu open, e.g. dark mode. */
export function MenuToggle({
  checked,
  onChange,
  icon,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <MenuSection
      selectionMode="multiple"
      selectedKeys={checked ? ["on"] : []}
      onSelectionChange={(keys) => onChange(keys !== "all" && keys.has("on"))}
      shouldCloseOnSelect={false}
    >
      <Item
        id="on"
        textValue={typeof children === "string" ? children : undefined}
        className={cn(itemClass, "justify-between gap-3")}
      >
        <span className="flex items-center gap-2">
          {icon}
          {children}
        </span>
        <span
          aria-hidden
          className={cn(
            "relative h-5 w-9 shrink-0 rounded-full transition-colors",
            checked ? "bg-accent" : "bg-border-strong",
          )}
        >
          <span
            className={cn(
              "absolute top-0.5 block size-4 rounded-full bg-white shadow-sm transition-transform",
              checked ? "translate-x-[18px]" : "translate-x-0.5",
            )}
          />
        </span>
      </Item>
    </MenuSection>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <Header className="px-2 pt-1.5 pb-1 text-[11.5px] font-medium text-fg-subtle">{children}</Header>;
}

export function MenuSeparator() {
  return <Separator className="my-1 h-px bg-border" />;
}
